import React, { useMemo, useEffect, useState } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Box, 
  Typography, 
  Grid, 
  Paper,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import MapIcon from '@mui/icons-material/Map';
import { SavedLocation } from '../types';
import PrintReportMap from './PrintReportMap';
import { format } from 'date-fns';
import { Marker, Source, Layer } from 'react-map-gl';
import * as GeoJSON from 'geojson';
import { calculateRoutes, calculateRouteFromCurrentLocation, LocationRouteInfo } from '../services/mapboxService';

// Helper function to create Google Maps navigation link
const getGoogleMapsLink = (lat: number, lon: number): string => {
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lon}`;
};

// Helper function to calculate cardinal direction when it's not available from the API
const getCardinalDirection = (angle: number): string => {
  // Normalize angle to 0-360 range
  const normalizedAngle = ((angle % 360) + 360) % 360;
  
  // Define cardinal directions with their angle ranges
  const directions = [
    { start: 337.5, end: 22.5, direction: "N" },
    { start: 22.5, end: 67.5, direction: "NE" },
    { start: 67.5, end: 112.5, direction: "E" },
    { start: 112.5, end: 157.5, direction: "SE" },
    { start: 157.5, end: 202.5, direction: "S" },
    { start: 202.5, end: 247.5, direction: "SW" },
    { start: 247.5, end: 292.5, direction: "W" },
    { start: 292.5, end: 337.5, direction: "NW" }
  ];
  
  // Find the matching direction
  for (const { start, end, direction } of directions) {
    if (start > end) { // Handle the wrap-around case (N)
      if (normalizedAngle >= start || normalizedAngle < end) {
        return direction;
      }
    } else if (normalizedAngle >= start && normalizedAngle < end) {
      return direction;
    }
  }
  
  return "N"; // Default fallback
};

// Helper function to get opposite cardinal direction
const getOppositeCardinalDirection = (direction: string): string => {
  const opposites: { [key: string]: string } = {
    'N': 'S',
    'NE': 'SW',
    'E': 'W',
    'SE': 'NW',
    'S': 'N',
    'SW': 'NE',
    'W': 'E',
    'NW': 'SE'
  };
  return opposites[direction] || direction;
};

// Helper function to format cardinal direction pair
const formatCardinalDirectionPair = (direction: string): string => {
  return `${direction}-${getOppositeCardinalDirection(direction)}`;
};

// Numbered marker component for locations
const NumberedMarker: React.FC<{ index: number }> = ({ index }) => (
  <Box sx={{ 
    width: 24, 
    height: 24, 
    borderRadius: '50%', 
    bgcolor: '#e53e3e', 
    color: 'white',
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 'bold',
    boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
    transform: 'translate(-50%, -50%)'
  }}>
    {index + 1}
  </Box>
);

interface PrintableReportProps {
  open: boolean;
  onClose: () => void;
  savedLocations: SavedLocation[];
}

const PrintableReport: React.FC<PrintableReportProps> = ({
  open,
  onClose,
  savedLocations
}) => {
  // State for route calculations
  const [routeInfo, setRouteInfo] = useState<{[key: string]: LocationRouteInfo}>({});
  const [routeGeometry, setRouteGeometry] = useState<{
    betweenLocations: any | null;
    fromCurrentLocation: any | null;
  }>({
    betweenLocations: null,
    fromCurrentLocation: null
  });
  const [isCalculatingRoutes, setIsCalculatingRoutes] = useState(false);
  
  // State for user's current location
  const [userLocation, setUserLocation] = useState<{latitude: number, longitude: number} | null>(null);
  
  // Get user's current location on mount
  useEffect(() => {
    if (open) {
      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setUserLocation({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude
            });
          },
          (error) => {
            console.error("Error getting user location:", error);
          }
        );
      }
    }
  }, [open]);

  // Fetch route data when the component opens
  useEffect(() => {
    if (open && savedLocations.length >= 2) {
      setIsCalculatingRoutes(true);
      
      const fetchRoutes = async () => {
        try {
          // Sort locations by order property for consistent routing
          const sortedLocations = [...savedLocations].sort((a, b) => {
            const orderA = a.order !== undefined ? a.order : 999;
            const orderB = b.order !== undefined ? b.order : 999;
            return orderA - orderB;
          });
          
          // First calculate routes between saved locations
          const { routes: betweenLocationsRoutes, routeGeometry: betweenLocationsGeometry } = 
            await calculateRoutes(sortedLocations);
          
          // Create the initial combined objects
          const combinedRouteInfo: {[key: string]: LocationRouteInfo} = { ...betweenLocationsRoutes };
          const combinedGeometry = {
            betweenLocations: betweenLocationsGeometry,
            fromCurrentLocation: null as any
          };
          
          // Now calculate route from current browser location to first location if we have user location
          if (userLocation && sortedLocations.length > 0) {
            const firstLocation = sortedLocations[0];
            const { routeInfo: currentLocationRouteInfo, routeGeometry: fromCurrentLocationGeometry } = 
              await calculateRouteFromCurrentLocation(
                userLocation,
                {
                  latitude: firstLocation.latitude,
                  longitude: firstLocation.longitude
                }
              );
            
            // If we got the current location route, add it to the route info
            if (currentLocationRouteInfo) {
              // Store the route info with a special key for the first location
              combinedRouteInfo[`current_to_${firstLocation.id}`] = currentLocationRouteInfo;
              combinedGeometry.fromCurrentLocation = fromCurrentLocationGeometry;
            }
          }
          
          setRouteInfo(combinedRouteInfo);
          setRouteGeometry(combinedGeometry);
        } catch (error) {
          console.error('Error calculating routes for report:', error);
          // Fall back to simple straight-line calculation
          const fallbackResult = calculateStraightLineDistances(savedLocations);
          setRouteInfo(fallbackResult.routes);
          setRouteGeometry({
            betweenLocations: fallbackResult.routeGeometry,
            fromCurrentLocation: null
          });
        } finally {
          setIsCalculatingRoutes(false);
        }
      };
      
      fetchRoutes();
    }
  }, [open, savedLocations, userLocation]);

  // Function to handle printing
  const handlePrint = () => {
    window.print();
  };

  // Calculate center of all locations for the overview map
  const getCenterCoordinates = () => {
    if (savedLocations.length === 0) {
      return { lat: 47.6062, lng: -122.3321 }; // Default coordinates
    }
    
    if (savedLocations.length === 1) {
      return { lat: savedLocations[0].latitude, lng: savedLocations[0].longitude };
    }
    
    // Calculate average of all coordinates
    const sumLat = savedLocations.reduce((sum, loc) => sum + loc.latitude, 0);
    const sumLng = savedLocations.reduce((sum, loc) => sum + loc.longitude, 0);
    
    return {
      lat: sumLat / savedLocations.length,
      lng: sumLng / savedLocations.length
    };
  };

  // Calculate appropriate zoom level based on the spread of locations
  const getAppropriateZoom = () => {
    if (savedLocations.length <= 1) return 11; // One zoom level lower than before
    
    // Determine the bounds
    const latitudes = savedLocations.map(loc => loc.latitude);
    const longitudes = savedLocations.map(loc => loc.longitude);
    
    const maxLat = Math.max(...latitudes);
    const minLat = Math.min(...latitudes);
    const maxLng = Math.max(...longitudes);
    const minLng = Math.min(...longitudes);
    
    // Calculate the distance with padding
    const latDiff = (maxLat - minLat) * 1.2; // Add 20% padding
    const lngDiff = (maxLng - minLng) * 1.2;
    
    // Define zoom levels based on the distance, one level lower than before
    if (latDiff > 10 || lngDiff > 10) return 3;
    if (latDiff > 5 || lngDiff > 5) return 4;
    if (latDiff > 1 || lngDiff > 1) return 6;
    if (latDiff > 0.5 || lngDiff > 0.5) return 8;
    if (latDiff > 0.1 || lngDiff > 0.1) return 10;
    return 12;
  };

  // Create route line for the map
  const routeLineGeoJSON = useMemo((): GeoJSON.Feature<GeoJSON.LineString> => {
    if (savedLocations.length < 2) {
      return {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: []
        }
      };
    }

    // If we have route geometry from the API, use it
    if (routeGeometry.betweenLocations) {
      return {
        type: 'Feature',
        properties: {},
        geometry: routeGeometry.betweenLocations as GeoJSON.LineString
      };
    }

    // Sort locations by order property
    const sortedLocations = [...savedLocations].sort((a, b) => {
      const orderA = a.order !== undefined ? a.order : 999;
      const orderB = b.order !== undefined ? b.order : 999;
      return orderA - orderB;
    });

    // Create a LineString from the locations
    return {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: sortedLocations.map(loc => [loc.longitude, loc.latitude])
      }
    };
  }, [savedLocations, routeGeometry.betweenLocations]);

  // Create route line for the current location to first location
  const currentLocationRouteGeoJSON = useMemo((): GeoJSON.Feature<GeoJSON.LineString> => {
    if (savedLocations.length < 1 || !routeGeometry.fromCurrentLocation) {
      return {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: []
        }
      };
    }

    // If we have route geometry from the API, use it
    return {
      type: 'Feature',
      properties: {},
      geometry: routeGeometry.fromCurrentLocation as GeoJSON.LineString
    };
  }, [savedLocations, routeGeometry.fromCurrentLocation]);

  const center = getCenterCoordinates();
  const zoom = getAppropriateZoom();
  
  // Sort locations for display
  const sortedLocations = useMemo(() => {
    return [...savedLocations].sort((a, b) => {
      const orderA = a.order !== undefined ? a.order : 999;
      const orderB = b.order !== undefined ? b.order : 999;
      return orderA - orderB;
    });
  }, [savedLocations]);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '80vh',
          maxHeight: '90vh',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ 
        m: 0, 
        p: 2, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid #e2e8f0'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Saved Locations Report
        </Typography>
        <Box>
          <Tooltip title="Print Report">
            <IconButton onClick={handlePrint} sx={{ mr: 1 }}>
              <PrintIcon />
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3, overflow: 'auto' }}>
        {/* Main map showing all locations */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 2, 
            mb: 3,
            height: '400px',
            position: 'relative'
          }}
        >
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            All Locations Overview
          </Typography>
          <Box sx={{ height: 'calc(100% - 32px)', position: 'relative' }}>
            {isCalculatingRoutes && (
              <Box sx={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                right: 0, 
                bottom: 0, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                zIndex: 10
              }}>
                <CircularProgress size={40} />
                <Typography sx={{ ml: 2 }}>Calculating routes...</Typography>
              </Box>
            )}
            <PrintReportMap
              latitude={center.lat}
              longitude={center.lng}
              zoom={zoom}
            >
              {/* Route line between locations */}
              {savedLocations.length >= 2 && (
                <Source
                  id="route-line" 
                  type="geojson" 
                  data={routeLineGeoJSON}
                >
                  <Layer
                    id="route-layer"
                    type="line"
                    paint={{
                      'line-color': 'rgba(255, 0, 0, 0.6)',
                      'line-width': 4,
                      'line-opacity': 0.7
                    }}
                  />
                </Source>
              )}
              
              {/* Route line from current location */}
              {routeGeometry.fromCurrentLocation && routeInfo[`current_to_${sortedLocations[0]?.id}`] && (
                <Source
                  id="current-route-line" 
                  type="geojson" 
                  data={currentLocationRouteGeoJSON}
                >
                  <Layer
                    id="current-route-layer"
                    type="line"
                    paint={{
                      'line-color': 'rgba(0, 0, 255, 0.6)',
                      'line-width': 4,
                      'line-opacity': 0.7
                    }}
                  />
                </Source>
              )}
              
              {/* Current user location marker */}
              {userLocation && (
                <Marker
                  latitude={userLocation.latitude}
                  longitude={userLocation.longitude}
                  anchor="center"
                >
                  <Box sx={{ 
                    width: 12, 
                    height: 12, 
                    borderRadius: '50%', 
                    bgcolor: '#1976d2',
                    boxShadow: '0 0 4px rgba(0,0,0,0.4)',
                  }} />
                </Marker>
              )}
              
              {sortedLocations.map((location, index) => (
                <Marker
                  key={location.id}
                  latitude={location.latitude}
                  longitude={location.longitude}
                  anchor="center"
                >
                  <NumberedMarker index={index} />
                </Marker>
              ))}
            </PrintReportMap>
          </Box>
        </Paper>

        {/* Individual location details */}
        <Grid container spacing={2}>
          {sortedLocations.map((location, index) => (
            <Grid item xs={12} key={location.id}>
              <Paper 
                elevation={2} 
                sx={{ 
                  p: 2,
                  pageBreakInside: 'avoid',
                  minHeight: '160px'
                }}
              >
                <Grid container spacing={2}>
                  {/* Location details */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, fontSize: '1rem' }}>
                      Location {index + 1}: {location.name}
                    </Typography>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        <a 
                          href={getGoogleMapsLink(location.latitude, location.longitude)}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: 'inherit', textDecoration: 'underline' }}
                        >
                          Navigate to: {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                        </a>
                      </Typography>
                      
                      {/* Route information */}
                      {index > 0 && routeInfo[location.id] && (
                        <Typography variant="body2" color="primary" sx={{ 
                          mb: 0.5, 
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          bgcolor: 'rgba(25, 118, 210, 0.1)',
                          p: 0.5,
                          borderRadius: 1,
                          fontWeight: 500
                        }}>
                          <DirectionsCarIcon sx={{ fontSize: '1rem' }} />
                          <span style={{ flexGrow: 1 }}>
                            Driving from previous: {routeInfo[location.id].time} ({routeInfo[location.id].distance})
                          </span>
                          <a 
                            href={getGoogleMapsLink(location.latitude, location.longitude)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: 'inherit', display: 'flex', alignItems: 'center' }}
                          >
                            <MapIcon sx={{ fontSize: '1rem' }} />
                          </a>
                        </Typography>
                      )}
                      
                      {/* Route information from current location (only for first location) */}
                      {index === 0 && routeInfo[`current_to_${location.id}`] && (
                        <Typography variant="body2" color="primary" sx={{ 
                          mb: 0.5, 
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          bgcolor: 'rgba(25, 118, 210, 0.1)',
                          p: 0.5,
                          borderRadius: 1,
                          fontWeight: 500
                        }}>
                          <DirectionsCarIcon sx={{ fontSize: '1rem' }} />
                          <span style={{ flexGrow: 1 }}>
                            From current location: {routeInfo[`current_to_${location.id}`].time} ({routeInfo[`current_to_${location.id}`].distance})
                          </span>
                          <a 
                            href={getGoogleMapsLink(location.latitude, location.longitude)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: 'inherit', display: 'flex', alignItems: 'center' }}
                          >
                            <MapIcon sx={{ fontSize: '1rem' }} />
                          </a>
                        </Typography>
                      )}
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Date: {format(new Date(location.selected_date), 'MMMM d, yyyy')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Row Direction: {location.row_direction.toFixed(1)}° ({formatCardinalDirectionPair(location.cardinal_direction || getCardinalDirection(location.row_direction))})
                      </Typography>
                      {location.time && (
                        <Typography variant="body2" sx={{ 
                          color: '#1E40AF', // Darker blue
                          fontWeight: 500, 
                          mb: 0.5,
                          bgcolor: '#DBEAFE', // Light blue background
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          display: 'inline-block',
                          mr: 1,
                          border: '1px solid #93C5FD' // Medium blue border
                        }}>
                          Time of Day: {location.time}
                        </Typography>
                      )}
                      {location.optimal_time_windows && location.optimal_time_windows.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" sx={{ 
                            color: '#065F46', // Darker green
                            fontWeight: 500,
                            mb: 0.25
                          }}>
                            Optimal Time Windows:
                          </Typography>
                          {location.optimal_time_windows.map((window, idx) => (
                            <Typography 
                              key={idx} 
                              variant="body2" 
                              sx={{ 
                                color: '#065F46', // Darker green
                                fontWeight: 500,
                                bgcolor: '#D1FAE5', // Light green background
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                                display: 'inline-block',
                                mr: 1,
                                mb: 0.5,
                                border: '1px solid #6EE7B7' // Medium green border
                              }}
                            >
                              {window.start} - {window.end}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Grid>
                  
                  {/* Location map */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ height: '200px', borderRadius: 1, overflow: 'hidden' }}>
                      <PrintReportMap
                        latitude={location.latitude}
                        longitude={location.longitude}
                        zoom={15}
                        satellite={true}
                      >
                        <Marker
                          latitude={location.latitude}
                          longitude={location.longitude}
                          anchor="center"
                        >
                          <NumberedMarker index={index} />
                        </Marker>
                      </PrintReportMap>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ p: 2, borderTop: '1px solid #e2e8f0' }}>
        <Button onClick={onClose}>Close</Button>
        <Button 
          variant="contained" 
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          Print Report
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Helper function for calculating straight-line distances between locations
const calculateStraightLineDistances = (
  locations: SavedLocation[]
): { 
  routes: {[key: string]: LocationRouteInfo}, 
  routeGeometry: any 
} => {
  const routeResults: {[key: string]: LocationRouteInfo} = {};
  const coordinates: [number, number][] = [];
  
  // Add all location coordinates to the geometry
  locations.forEach(loc => {
    coordinates.push([loc.longitude, loc.latitude]);
  });

  // Calculate routes between consecutive locations
  locations.forEach((location, index) => {
    if (index > 0) {
      const prevLocation = locations[index - 1];
      
      // Calculate distance in km (simple Euclidean distance)
      const dx = (location.longitude - prevLocation.longitude) * 111.32 * Math.cos(location.latitude * (Math.PI / 180));
      const dy = (location.latitude - prevLocation.latitude) * 110.574;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      // Convert km to meters
      const distanceInMeters = distance * 1000;
      
      // Estimate time (assuming 60 km/h average speed)
      const timeInHours = distance / 60;
      const timeInMinutes = Math.round(timeInHours * 60);
      const durationInSeconds = timeInMinutes * 60;
      
      routeResults[location.id] = {
        distance: `${distance.toFixed(1)} km`,
        time: timeInMinutes < 60 
          ? `${timeInMinutes} min` 
          : `${Math.floor(timeInMinutes / 60)}h ${timeInMinutes % 60}min`,
        durationSeconds: durationInSeconds,
        distanceMeters: distanceInMeters
      };
    }
  });

  // Create a simple straight-line GeoJSON geometry
  const routeGeometry = {
    type: 'LineString',
    coordinates: coordinates
  };

  return {
    routes: routeResults,
    routeGeometry: routeGeometry
  };
};

export default PrintableReport; 